import { useEffect, useState } from 'react';
import { ConnectorType, metaMask, hooks } from '../../connectors';
import { useWeb3ReactLegacy } from '../../utils/web3-compat';
import {
  attemptEagerConnection,
  activateConnector,
} from '../../connectors/utils';
import { LegacyWeb3ReactInterface } from '../../connectors/types';
import { IS_MAINNET, EL_TESTNET_NAME } from '../../utils/envVars';

export enum NetworkChainId {
  'Mainnet' = 1,
  'Ropsten' = 3,
  'Goerli' = 5,
  'TestnetV3' = 942,
  'TestnetV4' = 943,
  'PulseChain' = 369,
}

/*
  for UI purposes, all networks are "supported", but an error message
 is displayed when the user is not connected to the "allowed" network
 */

const supportedNetworks = [
  NetworkChainId.Mainnet,
  NetworkChainId.Ropsten,
  NetworkChainId.Goerli,
  NetworkChainId.TestnetV3,
  NetworkChainId.TestnetV4,
  NetworkChainId.PulseChain,
];

// WalletConnect v2 supports all networks that MetaMask does
// const walletConnectSupportedNetworks = supportedNetworks;

enum Testnet {
  'Ropsten',
  'Goerli',
  'TestnetV3',
  'TestnetV4',
}

enum Mainnet {
  'Mainnet',
  'PulseChain',
}

export const NetworkNameToChainId: { [key: string]: NetworkChainId } = {
  PulseChain: NetworkChainId.PulseChain,
  Mainnet: NetworkChainId.Mainnet,
  Ropsten: NetworkChainId.Ropsten,
  Goerli: NetworkChainId.Goerli,
  TestnetV3: NetworkChainId.TestnetV3,
  TestnetV4: NetworkChainId.TestnetV4,
};

export const TARGET_NETWORK_CHAIN_ID = IS_MAINNET
  ? NetworkChainId.PulseChain
  : NetworkNameToChainId[EL_TESTNET_NAME];

export const IS_GOERLI = TARGET_NETWORK_CHAIN_ID === NetworkChainId.Goerli;

export const AllowedNetworks = IS_MAINNET ? Mainnet : Testnet;

// Export connectors for backward compatibility (components expect these names)
export const metamask = metaMask;
export const portis = null; // Temporarily disabled due to compatibility
export const fortmatic = null; // Temporarily disabled due to compatibility
export const walletConnect = null; // Temporarily disabled due to Babel compatibility

// Enhanced eager connection with v8 support
export function useMetamaskEagerConnect(): boolean {
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    const attemptConnection = async () => {
      try {
        await attemptEagerConnection();
        setAttempted(true);
      } catch (error) {
        setAttempted(true);
      }
    };

    attemptConnection();
  }, []);

  return attempted;
}

// Enhanced MetaMask listener with v8 support
export function useMetamaskListener(suppress: boolean = false) {
  const { useAccount, useIsActive, useIsActivating } = hooks.metaMask;
  const account = useAccount();
  const isActive = useIsActive();
  const isActivating = useIsActivating();

  useEffect(() => {
    const { ethereum } = window as any;

    if (ethereum && ethereum.on && !isActive && !isActivating && !suppress) {
      const connectToMetamask = async () => {
        try {
          await activateConnector(ConnectorType.METAMASK);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('MetaMask connection failed:', error);
        }
      };

      const handleAccountsChanged = (accounts: string[]) => {
        if (accounts.length > 0 && !isActive) {
          connectToMetamask();
        }
      };

      const handleChainChanged = (chainId: string) => {
        // Force a refresh on chain change to ensure proper state sync
        window.location.reload();
      };

      const handleConnect = (connectInfo: { chainId: string }) => {
        if (!isActive) {
          connectToMetamask();
        }
      };

      const handleDisconnect = (error: { code: number; message: string }) => {
        // Handle disconnect if needed
      };

      // Add event listeners
      ethereum.on('connect', handleConnect);
      ethereum.on('disconnect', handleDisconnect);
      ethereum.on('chainChanged', handleChainChanged);
      ethereum.on('accountsChanged', handleAccountsChanged);

      return () => {
        if (ethereum.removeListener) {
          ethereum.removeListener('connect', handleConnect);
          ethereum.removeListener('disconnect', handleDisconnect);
          ethereum.removeListener('chainChanged', handleChainChanged);
          ethereum.removeListener('accountsChanged', handleAccountsChanged);
        }
      };
    }
  }, [isActive, isActivating, suppress, account]);
}

// Helper function to get web3React compatible interface
export function useWeb3ReactCompat(): LegacyWeb3ReactInterface {
  return useWeb3ReactLegacy();
}

// Network validation helpers
export function isNetworkSupported(chainId: number): boolean {
  return supportedNetworks.includes(chainId as NetworkChainId);
}

export function isCorrectNetwork(chainId: number): boolean {
  return chainId === TARGET_NETWORK_CHAIN_ID;
}

export function getNetworkName(chainId: number): string {
  switch (chainId) {
    case NetworkChainId.PulseChain:
      return 'PulseChain';
    case NetworkChainId.Mainnet:
      return 'Ethereum Mainnet';
    case NetworkChainId.Goerli:
      return 'Goerli Testnet';
    case NetworkChainId.Ropsten:
      return 'Ropsten Testnet';
    case NetworkChainId.TestnetV3:
      return 'PulseChain Testnet v3';
    case NetworkChainId.TestnetV4:
      return 'PulseChain Testnet v4';
    default:
      return `Unknown Network (${chainId})`;
  }
}

// Connection status helpers
export function isWalletConnected(account?: string, chainId?: number): boolean {
  return Boolean(account && chainId);
}

export function getConnectionError(chainId?: number): string | null {
  if (!chainId) return 'No wallet connected';
  if (!isNetworkSupported(chainId)) return 'Unsupported network';
  if (!isCorrectNetwork(chainId)) {
    const targetName = getNetworkName(TARGET_NETWORK_CHAIN_ID);
    return `Please switch to ${targetName}`;
  }
  return null;
}

// Legacy compatibility exports (for components that import directly)
export const injected = metamask; // Some components may import as 'injected'
